// OEEPanel.js — OLM1 INBOUND OEE (RME OEE_FrontEnd_V3_00), under the Sorter Map.
// Data: OEE_<date>/<shift> written by backend/oee_sync.py every ~5 min:
//   latest: { ts, window, area: {oee, quality, performance, availability, states}, rows: [...] }
//   history: [{ ts, area: {oee}, p1: {...}, p2: {...} }]
// View: Area (INBOUND) or one PID — 4 gauges, production-state pie, state bar,
// OEE trend for the shift, and the MP table (PIDs first; all MPs on demand).
// Timespan: Live = the rolling last hour; any other span aggregates the hourly
// records (OEE_HOURLY_<date>/<shift>, one per completed clock hour) via oeeAggregate.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { aggregateHours } from '../oeeAggregate';

const STATES = [
  { k: 'producing', label: 'Producing', color: '#2BB24C' },
  { k: 'idle', label: 'Idle', color: '#7B4A12' },
  { k: 'blocked', label: 'Blocked', color: '#B5A800' },
  { k: 'starved', label: 'Starved', color: '#FF8C00' },
  { k: 'downtime', label: 'Downtime / faulted', color: '#C62828' },
];
const VIEWS = { area: 'INBOUND (area)', PID1: 'PID1', PID2: 'PID2' };
const SPANS = { live: 'Live (last hour)', shift: 'Selected shift', day: 'Selected day (both shifts)', week: 'Last 7 days', custom: 'Custom dates' };
const pad = n => String(n).padStart(2, '0');
const ymd = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
function daysBetween(from, to, cap = 31) {
  const out = [], d = new Date(`${from}T12:00:00`), end = new Date(`${to}T12:00:00`);
  while (d <= end && out.length < cap) { out.push(ymd(d)); d.setDate(d.getDate() + 1); }
  return out;
}
const fmt = (v, d = 1) => (v == null || Number.isNaN(v) ? '—' : Number(v).toFixed(d));
const int = v => (v == null ? '—' : Math.round(v).toLocaleString());

function Gauge({ label, value, size = 1 }) {
  const r = 46 * size, cx = 60 * size, cy = 56 * size, w = 12 * size;
  const f = Math.max(0, Math.min(1, (value || 0) / 100));
  const pt = t => [cx - r * Math.cos(Math.PI * t), cy - r * Math.sin(Math.PI * t)];
  const [x0, y0] = pt(0), [x1, y1] = pt(1), [xv, yv] = pt(f);
  const col = value == null ? '#BBC0C1' : value >= 75 ? '#2BB24C' : value >= 50 ? '#F2C23E' : '#64B5F6';
  return (
    <svg viewBox={`0 0 ${120 * size} ${76 * size}`} style={{ width: 120 * size }} role="img" aria-label={`${label} ${fmt(value, 2)} percent`}>
      <path d={`M${x0},${y0} A${r},${r} 0 0 1 ${x1},${y1}`} fill="none" stroke="#2A3640" strokeWidth={w} />
      {f > 0 && <path d={`M${x0},${y0} A${r},${r} 0 0 1 ${xv},${yv}`} fill="none" stroke={col} strokeWidth={w} />}
      <text x={cx} y={cy - 4 * size} fontSize={14 * size} fontWeight="700" textAnchor="middle" fill="#232F3E">{fmt(value, 2)}%</text>
      <text x={cx} y={cy + 16 * size} fontSize={11 * size} textAnchor="middle" fill="#697576">{label}</text>
    </svg>
  );
}

function Pie({ states }) {
  const parts = STATES.map(s => ({ ...s, v: Math.max(0, states?.[s.k] || 0) })).filter(s => s.v > 0);
  const tot = parts.reduce((a, s) => a + s.v, 0);
  if (!tot) return <p style={{ fontSize: 13, color: '#697576' }}>No state data.</p>;
  let a0 = -Math.PI / 2;
  const R = 70, C = 80;
  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
      <svg viewBox="0 0 160 160" style={{ width: 160 }} role="img" aria-label="Planned production time by state">
        {parts.map(s => {
          const a1 = a0 + (s.v / tot) * 2 * Math.PI;
          const big = a1 - a0 > Math.PI ? 1 : 0;
          const d = parts.length === 1
            ? `M${C - R},${C} a${R},${R} 0 1,0 ${2 * R},0 a${R},${R} 0 1,0 ${-2 * R},0`
            : `M${C},${C} L${C + R * Math.cos(a0)},${C + R * Math.sin(a0)} A${R},${R} 0 ${big} 1 ${C + R * Math.cos(a1)},${C + R * Math.sin(a1)} Z`;
          const mid = (a0 + a1) / 2; a0 = a1;
          return <g key={s.k}><path d={d} fill={s.color} stroke="#fff" strokeWidth="1"><title>{`${s.label}: ${fmt(s.v, 2)}%`}</title></path>
            {s.v / tot > 0.06 && <text x={C + R * 0.62 * Math.cos(mid)} y={C + R * 0.62 * Math.sin(mid) + 4} fontSize="11" fontWeight="700" textAnchor="middle" fill="#fff">{fmt((s.v / tot) * 100, 1)}%</text>}</g>;
        })}
      </svg>
      <div style={{ fontSize: 12 }}>
        {STATES.map(s => <div key={s.k}><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 5, background: s.color, marginRight: 6 }} />{s.label}: {fmt(states?.[s.k], 2)}%</div>)}
      </div>
    </div>
  );
}

function Trend({ history }) {
  const pts = (history || []).filter(p => p && p.ts);
  if (pts.length < 2) return <p style={{ fontSize: 12, color: '#697576' }}>OEE trend appears after a few snapshots (every ~5 min).</p>;
  const W = 520, H = 120, L = 30, R = 8, T = 8, B = 20;
  const t0 = pts[0].ts, t1 = pts[pts.length - 1].ts || t0 + 1;
  const x = t => L + ((t - t0) / Math.max(1, t1 - t0)) * (W - L - R);
  const y = v => T + (1 - Math.min(100, Math.max(0, v)) / 100) * (H - T - B);
  const line = get => pts.filter(p => get(p) != null).map(p => `${x(p.ts)},${y(get(p))}`).join(' ');
  const series = [
    { k: 'area', label: 'INBOUND', color: '#232F3E', get: p => p.area?.oee },
    { k: 'p1', label: 'PID1', color: '#077398', get: p => p.p1?.oee },
    { k: 'p2', label: 'PID2', color: '#FF8C00', get: p => p.p2?.oee },
  ];
  const hhmm = t => new Date(t * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', maxWidth: W }} role="img" aria-label="OEE trend this shift">
        {[0, 50, 100].map(v => <g key={v}><line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke="#E7E9E9" /><text x={L - 4} y={y(v) + 4} fontSize="10" textAnchor="end">{v}</text></g>)}
        <text x={L} y={H - 4} fontSize="10">{hhmm(t0)}</text><text x={W - R} y={H - 4} fontSize="10" textAnchor="end">{hhmm(t1)}</text>
        {series.map(s => <polyline key={s.k} fill="none" stroke={s.color} strokeWidth="2" points={line(s.get)} />)}
      </svg>
      <div style={{ display: 'flex', gap: 12, fontSize: 11 }}>{series.map(s => <span key={s.k}><span style={{ display: 'inline-block', width: 12, height: 3, background: s.color, marginRight: 4, verticalAlign: 'middle' }} />{s.label} OEE</span>)}</div>
    </div>
  );
}

export default function OEEPanel({ api, site, date, shift }) {
  const [data, setData] = useState(null);
  const [hourly, setHourly] = useState([]);
  const [span, setSpan] = useState('live');
  const [from, setFrom] = useState(date);
  const [to, setTo] = useState(date);
  const [view, setView] = useState('area');
  const [allMps, setAllMps] = useState(false);
  const get = useCallback((d, st) => fetch(`${api}/api/fclm/cache?site=${site}&date=${d}&shift_type=${st}`).then(x => (x.ok ? x.json() : null)).catch(() => null), [api, site]);
  const load = useCallback(async () => {
    const r = await get(`OEE_${date}`, shift);
    setData(r && r.latest ? r : null);
    if (span === 'live') { setHourly([]); return; }
    let keys = [];
    if (span === 'shift') keys = [[date, shift]];
    else {
      const days = span === 'day' ? [date] : span === 'week' ? daysBetween(ymd(new Date(new Date(`${date}T12:00:00`).getTime() - 6 * 864e5)), date) : daysBetween(from <= to ? from : to, from <= to ? to : from);
      keys = days.flatMap(d => [[d, 'day'], [d, 'night']]);
    }
    const res = await Promise.all(keys.map(([d, st]) => get(`OEE_HOURLY_${d}`, st)));
    setHourly(res.flatMap(x => (x && x.hours && typeof x.hours === 'object' ? Object.values(x.hours) : [])));
  }, [get, date, shift, span, from, to]);
  useEffect(() => { load(); const t = setInterval(load, 60 * 1000); return () => clearInterval(t); }, [load]);

  // What the gauges/pie/table show: the live rolling hour, or the aggregated span.
  const agg = useMemo(() => (span === 'live' ? null : aggregateHours(hourly)), [span, hourly]);
  const latest = span === 'live' ? data?.latest : agg ? { ts: agg.last, window: `${agg.hours} hour${agg.hours === 1 ? '' : 's'}`, area: agg.area, rows: agg.rows } : null;
  const history = span === 'live' ? data?.history : agg?.history;
  const rows = useMemo(() => latest?.rows || [], [latest]);
  const pick = useMemo(() => {
    if (view === 'area') return { ...(latest?.area || {}), states: latest?.area?.states || {} };
    const r = rows.find(x => (x.description || '').toUpperCase() === view);
    if (!r) return null;
    // Per-MP states come from the table (% of time); faulted shown as downtime.
    return { ...r, states: { producing: r.producing, idle: r.idle, blocked: r.blocked, starved: r.starved, downtime: r.faulted } };
  }, [view, latest, rows]);
  const shown = useMemo(() => {
    const pids = rows.filter(r => r.type === 'PID');
    return allMps ? [...pids, ...rows.filter(r => r.type !== 'PID')] : pids;
  }, [rows, allMps]);

  const card = { background: '#fff', border: '1px solid #E7E9E9', borderRadius: 8, padding: 16, marginBottom: 16 };
  const th = { textAlign: 'right', fontSize: 11, color: '#697576', padding: '5px 6px', borderBottom: '1px solid #E7E9E9', whiteSpace: 'nowrap' };
  const td = { fontSize: 12, padding: '5px 6px', borderBottom: '1px solid #E7E9E9', textAlign: 'right', whiteSpace: 'nowrap' };

  const spanControls = (
    <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
      <label>Timespan <select value={span} onChange={e => setSpan(e.target.value)} aria-label="OEE timespan">{Object.entries(SPANS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
      {span === 'custom' && <><input type="date" value={from} onChange={e => setFrom(e.target.value)} aria-label="From date" /> to <input type="date" value={to} onChange={e => setTo(e.target.value)} aria-label="To date" /></>}
    </span>
  );
  if (site !== 'OLM1') return null;
  if (!latest) return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, fontSize: 12 }}><strong style={{ fontSize: 14 }}>PID OEE — INBOUND</strong>{spanControls}</div>
      <p style={{ fontSize: 13, color: '#697576' }}>{span === 'live'
        ? `No OEE data for ${date} ${shift} yet (the DevSpace OEE loop writes every ~5 min while a shift runs).`
        : 'No hourly OEE records in this timespan yet (one is saved shortly after each completed hour).'}</p></div>
  );
  const age = Math.round((Date.now() / 1000 - latest.ts) / 60);
  const spanLabel = span === 'live' ? `RME OEE · last ${latest.window || '1 hour'} · updated ${age} min ago`
    : `${latest.window} of hourly records · ${new Date(agg.first * 1000).toLocaleString([], { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })} – ${new Date(agg.last * 1000).toLocaleString([], { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`;
  const bar = pick?.states || {};
  const barTot = STATES.reduce((a, s) => a + Math.max(0, bar[s.k] || 0), 0) || 1;
  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <strong style={{ fontSize: 14 }}>PID OEE — INBOUND</strong>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 12 }}>
          {spanControls}
          <label>View <select value={view} onChange={e => setView(e.target.value)} aria-label="OEE view">{Object.entries(VIEWS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
          <span style={{ color: span === 'live' && age > 15 ? '#C62828' : '#697576' }}>{spanLabel}</span>
        </div>
      </div>
      {!pick ? <p style={{ fontSize: 13 }}>{view} not in the latest snapshot.</p> : (
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start', marginTop: 8 }}>
          <div>
            <div style={{ display: 'flex', justifyContent: 'center' }}><Gauge label="OEE" value={pick.oee} size={1.4} /></div>
            <div style={{ display: 'flex', gap: 4 }}><Gauge label="Quality" value={pick.quality} /><Gauge label="Performance" value={pick.performance} /><Gauge label="Availability" value={pick.availability} /></div>
            {view !== 'area' && <div style={{ fontSize: 12, color: '#697576', textAlign: 'center' }}>Avg {int(pick.avgUph)} / {int(pick.theoreticalUph)} UPH · {int(pick.totalUnits)} units ({int(pick.goodUnits)} good)</div>}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Planned production time</div>
            <Pie states={bar} />
            <div style={{ display: 'flex', height: 14, width: 320, marginTop: 10, borderRadius: 3, overflow: 'hidden' }} aria-hidden="true">
              {STATES.map(s => (bar[s.k] > 0 ? <div key={s.k} title={`${s.label} ${fmt(bar[s.k], 2)}%`} style={{ width: `${(bar[s.k] / barTot) * 100}%`, background: s.color }} /> : null))}
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 320 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>{span === 'live' ? 'OEE this shift (rolling hour, every ~5 min)' : 'OEE by hour'}</div>
            <Trend history={history} />
          </div>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
        <strong style={{ fontSize: 13 }}>MPs in Area/Subarea</strong>
        <label style={{ fontSize: 12 }}><input type="checkbox" checked={allMps} onChange={e => setAllMps(e.target.checked)} /> Show all {rows.length} MPs</label>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', marginTop: 6 }}>
          <thead><tr>{['MP', 'Description', 'OEE', 'Quality', 'Perf', 'Avail', 'Avg UPH', 'Theor. UPH', 'Producing', 'Idle', 'Blocked', 'Starved', 'Faulted', 'Total units', 'Good units'].map((h, i) => <th key={h} style={{ ...th, textAlign: i < 2 ? 'left' : 'right' }}>{h}</th>)}</tr></thead>
          <tbody>
            {shown.map(r => (
              <tr key={`${r.mp}-${r.description}`} onClick={() => r.type === 'PID' && VIEWS[r.description] && setView(r.description)} style={{ cursor: r.type === 'PID' ? 'pointer' : 'default', background: view === r.description ? '#EAF6FB' : undefined }}>
                <td style={{ ...td, textAlign: 'left' }}>{int(r.mp)}</td><td style={{ ...td, textAlign: 'left' }}>{r.description}</td>
                <td style={{ ...td, fontWeight: 700 }}>{fmt(r.oee, 2)}</td><td style={td}>{fmt(r.quality, 2)}</td><td style={td}>{fmt(r.performance, 2)}</td><td style={td}>{fmt(r.availability, 2)}</td>
                <td style={td}>{int(r.avgUph)}</td><td style={td}>{int(r.theoreticalUph)}</td>
                <td style={td}>{fmt(r.producing, 1)}</td><td style={td}>{fmt(r.idle, 1)}</td><td style={td}>{fmt(r.blocked, 1)}</td><td style={td}>{fmt(r.starved, 1)}</td><td style={td}>{fmt(r.faulted, 1)}</td>
                <td style={td}>{int(r.totalUnits)}</td><td style={td}>{int(r.goodUnits)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 11, color: '#697576', marginTop: 6 }}>
        Source: RME OEE dashboard (OLM1 / INBOUND). Live = its rolling last hour; other timespans combine one record per completed hour (state %, availability and performance averaged over the hours, units summed, quality = good ÷ total, OEE = A × P × Q). State % = share of planned production time (Starved = PID not fed fast enough to reach capacity; Blocked = downstream conveyance faults, the sorter jams / full beds on the map above).
      </div>
    </div>
  );
}
